type BubbleType = 'image' | 'video' | 'voicenote' | 'file' | 'location' | 'text' | 'interactive' | 'contact';

type OutboundTranslatorType =
    'text'
    | 'media'
    | 'location'
    | 'button'
    | 'list'
    | 'contact'
    | 'requestlocation'
    | 'notification'
    | 'actionUrl';

interface OutboundTranslator {
    channelId: number;
    integrationId: string;
    to: string;
    type: OutboundTranslatorType;
    content: string | ContentLocation | ContentMedia | ContentInteractive | Contact;
    isBsuid?: boolean;
    triggerContactBookFlow?: boolean;
}

interface ContentLocation {
    contentType: string;
    locationAddress: any;
    locationName: any;
    latitude: any;
    longitude: any;
}

interface ContentInteractive {
    contentType: string;
    title: string;
    replyMessage: string;
    postbackData: string;
    messageDisplay: string;
    message?: string;
    relatedClientMessageId?: string;
}

interface ContentMedia {
    contentType: string;
    downloadUrl: string;
    sha256Hash: string;
    byteSize: number;
    fileName: string;
    caption: string;
    reference?: string;
}

interface Contact {
    contentType: string;
    addresses: [];
    emails: [];
    org: {
        company: string,
        department: string,
        title: string
    };
    phones: [];
    urls: [];
    birthday: string;
    name: {
        firstName: string,
        lastName: string,
        formattedName: string,
    };
}


interface TranslationResponse {
    channelPayload: any;
    mediaData: MediaData;
}

export interface MediaData {
    description?: string;
    fileSize?: number;
    caption?: string;
    type?: string;
    locationAddress?: any;
    locationName?: any;
    latitude?: any;
    longitude?: any;
    bubbleType: BubbleType | OutboundTranslatorType;
    src: string;
    reference?: string;
    fileHash?: string;
    subtitle?: string;
    title?: string;
    replyMessage?: string;
    postbackData?: string;
    messageDisplay?: string;
    message?: string;
    messageId?: string;
    contacts?: Contact;
    content?: string;
    statusCode?: string;
    actionUrl?: string;
    status?: string;
    clientMessageId?: string
    buttons?: ButtonBubble
    locationRequest?: LocationRequestBubble
}

export enum ButtonHeaderType {
    Image = 'image',
    Video = 'video',
    Pdf = 'pdf',
}

export interface ButtonBubbleHeader {
    type: ButtonHeaderType,
    src?: string
    description?: string
    fileSize?: string
    messageStatus: 'sent'
}

export interface ButtonBubble {
    items: {
        title: string;
        postbackData: string;
    }[];
    body?: string;
    footer?: string;
    text?: string;
    media?: ButtonBubbleHeader;
}

export interface LocationRequestBubble {
    text: string
    body: string
    src: string
}

const metaData = (content: ContentMedia | ContentLocation | ContentInteractive | Contact, messageId: string): MediaData => {
    const type = getContentType(content);
    switch (type) {
        case 'image':
            content = content as ContentMedia;
            return {
                src: content.downloadUrl,
                reference: content.reference,
                description: content.caption,
                fileHash: content.sha256Hash,
                messageId: messageId,
                bubbleType: 'image',
            };
        case 'video':
            content = content as ContentMedia;
            return {
                src: content.downloadUrl,
                reference: content.reference,
                type: content.contentType,
                fileHash: content.sha256Hash,
                messageId: messageId,
                bubbleType: 'video',
            };
        case 'audio':
            content = content as ContentMedia;
            return {
                src: content.downloadUrl,
                reference: content.reference,
                fileHash: content.sha256Hash,
                messageId: messageId,
                bubbleType: 'voicenote',
            };
        case 'application':
            content = content as ContentMedia;
            return {
                description: content.fileName,
                fileSize: content.byteSize,
                src: content.downloadUrl,
                reference: content.reference,
                fileHash: content.sha256Hash,
                type: content.contentType,
                messageId: messageId,
                bubbleType: 'file',
            };
        case 'location':
            content = content as ContentLocation;
            return {
                locationName: content.locationName,
                locationAddress: content.locationAddress,
                latitude: content.latitude,
                longitude: content.longitude,
                src: '',
                messageId: messageId,
                bubbleType: 'location',
            };
        case 'list':
            content = content as ContentInteractive;
            return {
                title: content.title,
                replyMessage: content.replyMessage,
                postbackData: content.postbackData,
                messageDisplay: content.messageDisplay,
                src: '',
                messageId: messageId,
                bubbleType: 'interactive',
            };
        case 'button':
            content = content as ContentInteractive;
            return {
                title: content.title,
                replyMessage: content.replyMessage,
                postbackData: content.postbackData,
                messageDisplay: content.messageDisplay,
                message: content.message,
                src: '',
                messageId: messageId,
                bubbleType: 'interactive',
            };
        case 'contact':
            return {
                src: '',
                messageId: messageId,
                contacts: content as Contact,
                bubbleType: 'contact',
            };
        default:
            return;
    }
};

const getContentType = (content: ContentMedia | ContentLocation | ContentInteractive | Contact) => {
    const contentType = content.contentType;
    if (contentType.includes('/')) {
        return content ? content.contentType.split('/')[0] : null;
    }

    return contentType;
};

export {
    getContentType,
    metaData,
    OutboundTranslator,
    TranslationResponse,
    ContentMedia,
    ContentInteractive,
    ContentLocation,
    Contact
};
